/**
 * Turns an attribute and value from a log event into a NRQL WHERE fragment.
 *
 * The fragments go into the same filter box a human types in, so they have to
 * be valid on their own — buildLogQuery parenthesises whatever it is given and
 * ANDs it after the hostname clause.
 */

/**
 * Log attributes are not all bare identifiers: `device.type`, `host.ip` and
 * `newrelic.logPattern` all appear on a single record. NRQL reads a dot as a
 * path, so those have to be backticked or the query silently matches nothing.
 */
export function nrqlIdentifier(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name)
    ? name
    : `\`${String(name).replace(/`/g, "")}\``;
}

/** Single quotes delimit NRQL strings, so one inside a value would end it. */
export function nrqlLiteral(value) {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return `'${String(value).replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
}

/**
 * `filter` builds `attr = value`, `exclude` builds `attr != value`.
 *
 * NULL is the exception: NRQL compares it with IS / IS NOT, and `= NULL` would
 * be false for every row rather than matching the records missing the
 * attribute — which is exactly what someone excluding a null is looking for.
 */
export function nrqlFragment(name, value, { negate = false } = {}) {
  const identifier = nrqlIdentifier(name);

  if (value === null || value === undefined) {
    return `${identifier} IS ${negate ? "NOT " : ""}NULL`;
  }

  if (typeof value === "object") {
    // An object or array has no sensible equality here; callers should not
    // offer the option, but a wrong fragment is worse than an honest miss.
    return null;
  }

  return `${identifier} ${negate ? "!=" : "="} ${nrqlLiteral(value)}`;
}

/**
 * Adds a fragment to whatever is already in the filter box.
 *
 * Appending with AND rather than replacing: narrowing down attribute by
 * attribute is the whole point of the interaction, and replacing would throw
 * away the click before. A fragment already present is not added twice.
 */
export function addFragment(existing, fragment) {
  const current = (existing || "").trim();
  if (!fragment) return current;
  if (!current) return fragment;

  const already = current
    .split(/\s+AND\s+/i)
    .map((part) => part.trim())
    .includes(fragment);

  return already ? current : `${current} AND ${fragment}`;
}
